<template>
  <IndicatorCard
    :loading="loading"
    :has-data="requestsByCostCenter.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhuma solicitação encontrada')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0" justify="center">
      <v-col cols="12">
        <strong>Total: {{ total }}</strong>

        <v-row justify="center">
          <v-col cols="12" lg="8">
            <BaseEChart :option="chartOption" :loading="loading" height="320px" />
          </v-col>
        </v-row>

        <div class="wrapper">
          <v-data-table
            :loading="loading"
            :loading-text="$t('Carregando...')"
            :no-data-text="$t('Nenhum centro de custo encontrado')"
            :headers="requestsByCostCenterHeader"
            :items="requestsByCostCenter"
            class="main-table"
            dense
            hide-default-footer
            :items-per-page="pageSize"
            :mobile-breakpoint="0"
          >
            <template #[`item.costCenter`]="{ item }">
              {{ item.costCenterName }}
            </template>

            <template #[`item.quantity`]="{ item }">
              {{ item.serviceOrdersQuantity }}
            </template>
          </v-data-table>

          <div class="pagination">
            <div class="item-pagination block-two">
              <v-pagination
                class="pagination-item"
                v-model="page"
                :length="totalPages"
                :total-visible="9"
              />
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <span>{{ $t('Análise crítica') }}</span>
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :toolbar-attributes="{ color: 'white', dark: false }"
            :card-props="{ flat: true, color: '#F5F5F5' }"
            :placeholder="$t('Insira aqui sua análise crítica')"
          />
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import {
  getIndicatorById,
  getIndicatorExportName,
} from '../../../../../indicators/indicatorRegistry'
import ApiService from '@/services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildBarOption } from '../../shared/chartConfigs'
import { variedBarColors } from '../../shared/echartsTheme'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  History,
  HardBreak,
} from 'tiptap-vuetify'

export default {
  name: 'RequestsPerCostCenter',

  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    review: String,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
  },

  data: () => ({
    excelLoading: false,
    loading: true,
    page: 1,
    totalPages: 1,
    pageSize: 7,
    startDate: null,
    endDate: null,
    requestsByCostCenter: [],
    filter: {},
    costCenterNames: [],
    requestQuantity: [],
    requestsByCostCenterHeader: [
      { text: '', align: 'center', value: '' },
      { text: 'Centro de Custo', align: 'center', value: 'costCenter' },
      { text: 'Quantidade', align: 'center', value: 'quantity' },
    ],
    apiService: new ApiService(),
    total: 0,
    extensions: [
      History, Link, Underline, Strike, Italic, ListItem, BulletList,
      OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold, Link, HardBreak,
    ],
  }),

  computed: {
    indicator() {
      return getIndicatorById('requests_per_cost_center') || {}
    },
    chartOption() {
      return buildBarOption({
        categories: this.costCenterNames,
        series: [
          {
            name: this.$t('Solicitações'),
            data: this.requestQuantity,
            colors: variedBarColors,
          },
        ],
        showLegend: false,
      })
    },
  },

  watch: {
    filterProp() {
      this.startDate = this.filterProp.startDate
      this.endDate = this.filterProp.endDate
      this.review = this.filterProp.review
    },

    review() {
      this.setInfos()
    },

    startDate() {
      this.filter.startDate = this.startDate + '-01'
      this.list()
    },

    endDate() {
      this.filter.endDate = this.endDate + '-01'
      this.list()
    },

    page() {
      this.list()
    },
  },

  created() {
    if (this.filterProp.startDate && this.filterProp.endDate) {
      this.startDate = this.filterProp.startDate.substring(0, 7)
      this.endDate = this.filterProp.endDate.substring(0, 7)
      this.review = this.filterProp.review
    } else {
      const now = new Date()
      const initialMonth = new Date(now.getFullYear(), 0, 1)
      this.startDate = initialMonth.toISOString().substring(0, 7)
      this.endDate = now.toISOString().substring(0, 7)
    }

    this.filter = {
      startDate: this.startDate + '-01',
      endDate: this.endDate + '-01',
      institutionId: this.institution.id,
      pageSize: this.pageSize,
      unidadeIds: this.unidades,
    }

    this.list()
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${getIndicatorExportName(
        'requests_per_cost_center',
        this.institution
      )}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setInfos() {
      this.$emit('setInfos', {
        chart: getIndicatorById('requests_per_cost_center').legacyKey,
        review: this.review,
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
      })
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
    },

    extractChartData(response) {
      this.total = response.content.total
      this.requestsByCostCenter = response.content.objects
      this.totalPages = response.content.pager.totalPages

      this.costCenterNames = []
      this.requestQuantity = []

      if (response.content.chartsModel?.length) {
        response.content.chartsModel.forEach((chart) => {
          this.costCenterNames.push(chart.name)
          this.requestQuantity.push(chart.quantity)
        })
      }

      this.setInfos()
    },

    async list() {
      this.loading = true
      try {
        this.filter.page = this.page
        this.filter.startDate = this.filterProp.startDate
        this.filter.endDate = this.filterProp.endDate

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/requestsByCostCenter',
          this.filter
        )
        this.extractChartData(response)
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  padding: 0.5rem;

  .pagination {
    margin: 1.5rem 0;
    background: #f5f5f5;
    border-radius: 4px;
    padding: 0.3rem 1.3rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .item-pagination {
      flex: 1 1 100px;
      margin: 0.5rem 0;
    }

    .block-two {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
}
</style>
